/**
 * Centralized Liveness Detection Configuration
 *
 * This file contains all thresholds used for liveness detection.
 * Both the main SDK and the debug page use these values.
 *
 * When calibrating on different devices:
 * 1. Open debug.html and test each action
 * 2. Adjust thresholds here based on observed values
 * 3. The SDK will automatically use the updated values
 */

// ============================================================
// Types
// ============================================================

/**
 * Per-action timing configuration
 * Each action can have its own window, hold time, and baseline collection time
 */
export interface ActionTimingConfig {
  /** Time window for completing the action in ms */
  windowMs: number;
  /** Time to hold the position (for head movements) in ms */
  holdMs?: number;
  /** Time to collect baseline before detection (for smile/brow) in ms */
  baselineMs?: number;
}

/**
 * Per-action timing configurations
 * Industry-standard values based on UX research:
 * - Blink: 2.5s (natural cycle ~400ms + instruction comprehension)
 * - Smile: 3.5s (expressions need time to form naturally)
 * - Head turns: 5s (physical movement + 300ms hold)
 * - Head tilts: 5s (similar to turns)
 * - Open mouth: 3s (quick but needs clarity)
 * - Raise brows: 4s (needs baseline + expression)
 */
export interface ActionTimings {
  blink: ActionTimingConfig;
  smile: ActionTimingConfig;
  openMouth: ActionTimingConfig;
  raiseBrows: ActionTimingConfig;
  lookLeft: ActionTimingConfig;
  lookRight: ActionTimingConfig;
  lookUp: ActionTimingConfig;
  lookDown: ActionTimingConfig;
}

export interface LivenessThresholds {
  // BLINK Detection
  /** EAR threshold - eyes closed when EAR falls below this (default: 0.18) */
  blinkEarThreshold: number;
  /** EAR must rise by this delta to confirm eye reopened (default: 0.07) */
  blinkReopenDelta: number;

  // HEAD TURNS (Yaw)
  // Note: In face-utils, NEGATIVE yaw = looking left, POSITIVE yaw = looking right
  /** Yaw angle threshold for left turn - triggers when yaw < -this (default: 35) */
  lookLeftAngle: number;
  /** Yaw angle threshold for right turn - triggers when yaw > +this (default: 35) */
  lookRightAngle: number;
  /** Hysteresis margin to prevent flickering during turn detection (default: 3) */
  yawHysteresis: number;

  // HEAD TILT (Pitch)
  /** Pitch angle for looking up - POSITIVE pitch (default: 35) */
  lookUpAngle: number;
  /** Pitch angle for looking down (default: 25) */
  lookDownAngle: number;
  /** Hysteresis margin for pitch detection (default: 3) */
  pitchHysteresis: number;

  // MOUTH Actions
  /** Smile delta from baseline - triggers when mouth width increases by this (default: 0.015) */
  smileThreshold: number;
  /** MAR threshold for open mouth detection (default: 0.09) */
  openMouthMarThreshold: number;

  // EYEBROWS
  /** Brow raise delta from baseline - negative because brows move UP (default: 0.006) */
  raiseBrowsThreshold: number;

  // LIGHTING
  /** Minimum brightness (0-255) - too dark if below (default: 60) */
  minBrightness: number;
  /** Maximum brightness (0-255) - too bright if above (default: 220) */
  maxBrightness: number;
  /** Minimum variance - flat/weak lighting if below (default: 150) */
  minVariance: number;

  // TIMING (per-action)
  /** Per-action timing configuration */
  actionTimings: ActionTimings;

  // TIMING (legacy - kept for backward compatibility, maps to per-action values)
  /** @deprecated Use actionTimings.*.windowMs instead */
  actionWindowMs: number;
  /** @deprecated Use actionTimings.lookLeft.windowMs instead */
  headTurnWindowMs: number;
  /** @deprecated Use actionTimings.smile.baselineMs instead */
  baselineCollectionMs: number;
  /** @deprecated Use actionTimings.raiseBrows.baselineMs instead */
  browBaselineMs: number;
  /** @deprecated Use actionTimings.lookLeft.holdMs instead */
  headTurnHoldMs: number;

  // SMOOTHING
  /** Number of frames for yaw moving average (default: 5) */
  yawHistorySize: number;
  /** Number of frames for pitch moving average (default: 5) */
  pitchHistorySize: number;
}

// ============================================================
// Default Configuration
// ============================================================

/**
 * Default per-action timing configurations
 * Values based on industry standards and UX research:
 *
 * - BLINK (2.5s): Natural blink cycle is ~400ms. Extra time for instruction
 *   comprehension and hesitation. Users rarely need full window.
 *
 * - SMILE (3.5s): Facial expressions need time to form naturally.
 *   Baseline collection (300ms) + detection window.
 *
 * - HEAD TURNS (5s + 300ms hold): Physical head movement requires more time.
 *   Hold ensures intentional turn vs. accidental glance. Industry standard
 *   for KYC is 3-6 seconds with 200-500ms hold.
 *
 * - HEAD TILTS (5s + 300ms hold): Similar to turns, requires physical movement.
 *
 * - OPEN MOUTH (3s): Quick action but needs clear instruction comprehension.
 *
 * - RAISE BROWS (4s): Needs baseline collection (500ms) + expression formation.
 *   More subtle than smile, users need slightly more time.
 */
export const DEFAULT_ACTION_TIMINGS: ActionTimings = {
  blink: {
    windowMs: 2500,
  },
  smile: {
    windowMs: 3500,
    baselineMs: 300,
  },
  openMouth: {
    windowMs: 3000,
  },
  raiseBrows: {
    windowMs: 4000,
    baselineMs: 500,
  },
  lookLeft: {
    windowMs: 5000,
    holdMs: 300,
  },
  lookRight: {
    windowMs: 5000,
    holdMs: 300,
  },
  lookUp: {
    windowMs: 5000,
    holdMs: 300,
  },
  lookDown: {
    windowMs: 5000,
    holdMs: 300,
  },
};

export const DEFAULT_THRESHOLDS: LivenessThresholds = {
  // BLINK - Current values work well
  blinkEarThreshold: 0.18,
  blinkReopenDelta: 0.07,

  // HEAD TURNS - May need calibration per device
  // Face-utils convention: NEGATIVE yaw = looking left, POSITIVE yaw = looking right
  // LOOK_LEFT triggers when yaw < -lookLeftAngle
  lookLeftAngle: 35,
  // LOOK_RIGHT triggers when yaw > +lookRightAngle
  lookRightAngle: 35,
  yawHysteresis: 3,

  // HEAD TILT - May need calibration per device
  lookUpAngle: 33,
  lookDownAngle: 18,
  pitchHysteresis: 3,

  // MOUTH
  smileThreshold: 0.08,
  openMouthMarThreshold: 0.5,

  // EYEBROWS
  raiseBrowsThreshold: 0.02,

  // LIGHTING
  minBrightness: 60,
  maxBrightness: 220,
  minVariance: 150,

  // TIMING (per-action - preferred)
  actionTimings: DEFAULT_ACTION_TIMINGS,

  // TIMING (legacy - for backward compatibility, derived from per-action values)
  actionWindowMs: 3500, // Fallback: average of quick actions
  headTurnWindowMs: 5000, // Fallback: head movement window
  baselineCollectionMs: 300, // Fallback: smile baseline
  browBaselineMs: 500, // Fallback: brow baseline
  headTurnHoldMs: 300, // Fallback: head hold time

  // SMOOTHING
  yawHistorySize: 5,
  pitchHistorySize: 5,
};

// ============================================================
// Action Definitions (for debug page dropdown)
// ============================================================

export type LivenessAction =
  | 'BLINK'
  | 'LOOK_LEFT'
  | 'LOOK_RIGHT'
  | 'SMILE'
  | 'OPEN_MOUTH'
  | 'LOOK_UP'
  | 'LOOK_DOWN'
  | 'RAISE_BROWS';

export const LIVENESS_ACTIONS: LivenessAction[] = [
  'BLINK',
  'LOOK_LEFT',
  'LOOK_RIGHT',
  'SMILE',
  'OPEN_MOUTH',
  'LOOK_UP',
  'LOOK_DOWN',
  'RAISE_BROWS',
];

export interface ActionConfig {
  /** Display name for UI */
  name: string;
  /** Which threshold key to use */
  thresholdKey: keyof LivenessThresholds;
  /** Which timing config key to use for this action */
  timingKey: keyof ActionTimings;
  /** Human-readable hint */
  hint: string;
  /** The metric being measured */
  metric: 'EAR' | 'MAR' | 'YAW' | 'PITCH' | 'SMILE' | 'BROW';
  /** How to compare: 'less' = value < threshold triggers, 'greater' = value > threshold */
  comparison: 'less' | 'greater';
  /** Unit for display */
  unit: string;
  /** Slider min/max/step for debug page */
  sliderConfig: { min: number; max: number; step: number };
}

export const ACTION_CONFIGS: Record<LivenessAction, ActionConfig> = {
  BLINK: {
    name: 'Blink',
    thresholdKey: 'blinkEarThreshold',
    timingKey: 'blink',
    hint: 'Blink your eyes',
    metric: 'EAR',
    comparison: 'less',
    unit: '',
    sliderConfig: { min: 0.05, max: 0.4, step: 0.01 }, // ~0.5x to 2x of 0.18
  },
  LOOK_LEFT: {
    name: 'Look Left',
    thresholdKey: 'lookLeftAngle',
    timingKey: 'lookLeft',
    hint: 'Turn your head to the left',
    metric: 'YAW',
    comparison: 'less', // NEGATIVE yaw = looking left, so yaw < -threshold
    unit: '°',
    sliderConfig: { min: 15, max: 90, step: 1 },
  },
  LOOK_RIGHT: {
    name: 'Look Right',
    thresholdKey: 'lookRightAngle',
    timingKey: 'lookRight',
    hint: 'Turn your head to the right',
    metric: 'YAW',
    comparison: 'greater', // POSITIVE yaw = looking right, so yaw > +threshold
    unit: '°',
    sliderConfig: { min: 15, max: 90, step: 1 },
  },
  LOOK_UP: {
    name: 'Look Up',
    thresholdKey: 'lookUpAngle',
    timingKey: 'lookUp',
    hint: 'Look up',
    metric: 'PITCH',
    comparison: 'greater',
    unit: '°',
    sliderConfig: { min: 10, max: 70, step: 1 }, // ~0.5x to 2x of 35
  },
  LOOK_DOWN: {
    name: 'Look Down',
    thresholdKey: 'lookDownAngle',
    timingKey: 'lookDown',
    hint: 'Look down',
    metric: 'PITCH',
    comparison: 'less',
    unit: '°',
    sliderConfig: { min: 5, max: 40, step: 1 }, // ~0.25x to 2x of 18
  },
  SMILE: {
    name: 'Smile',
    thresholdKey: 'smileThreshold',
    timingKey: 'smile',
    hint: 'Smile',
    metric: 'SMILE',
    comparison: 'greater',
    unit: '',
    sliderConfig: { min: 0.02, max: 0.16, step: 0.005 }, // ~0.25x to 2x of 0.08
  },
  OPEN_MOUTH: {
    name: 'Open Mouth',
    thresholdKey: 'openMouthMarThreshold',
    timingKey: 'openMouth',
    hint: 'Open your mouth',
    metric: 'MAR',
    comparison: 'greater',
    unit: '',
    sliderConfig: { min: 0.2, max: 0.8, step: 0.01 }, // ~0.4x to 1.6x of 0.5
  },
  RAISE_BROWS: {
    name: 'Raise Eyebrows',
    thresholdKey: 'raiseBrowsThreshold',
    timingKey: 'raiseBrows',
    hint: 'Raise your eyebrows',
    metric: 'BROW',
    comparison: 'less', // Delta becomes MORE NEGATIVE when raising
    unit: '',
    sliderConfig: { min: 0.005, max: 0.04, step: 0.001 }, // ~0.25x to 2x of 0.02
  },
};

// ============================================================
// Lighting Configuration
// ============================================================

export interface LightingConfig {
  name: string;
  hint: string;
  metrics: Array<{
    key: 'brightness' | 'variance';
    label: string;
    thresholdKey: keyof LivenessThresholds;
    comparison: 'min' | 'max' | 'range';
    unit: string;
    sliderConfig: { min: number; max: number; step: number };
  }>;
}

export const LIGHTING_CONFIG: LightingConfig = {
  name: 'Lighting',
  hint: 'Check lighting conditions',
  metrics: [
    {
      key: 'brightness',
      label: 'Brightness',
      thresholdKey: 'minBrightness',
      comparison: 'range',
      unit: '',
      sliderConfig: { min: 20, max: 120, step: 5 }, // ~0.5x to 2x of 60
    },
    {
      key: 'variance',
      label: 'Variance',
      thresholdKey: 'minVariance',
      comparison: 'min',
      unit: '',
      sliderConfig: { min: 50, max: 350, step: 10 }, // ~0.5x to 2x of 150
    },
  ],
};

// ============================================================
// Utility: Get current thresholds (allows runtime override)
// ============================================================

let runtimeOverrides: Partial<LivenessThresholds> = {};
let runtimeTimingOverrides: Partial<ActionTimings> = {};

/**
 * Get current threshold value (with any runtime overrides)
 */
export function getThreshold<K extends keyof LivenessThresholds>(key: K): LivenessThresholds[K] {
  return (runtimeOverrides[key] ?? DEFAULT_THRESHOLDS[key]) as LivenessThresholds[K];
}

/**
 * Get timing config for a specific action
 * Maps LivenessAction to ActionTimings key
 */
export function getActionTiming(action: LivenessAction): ActionTimingConfig {
  const actionToKey: Record<LivenessAction, keyof ActionTimings> = {
    BLINK: 'blink',
    SMILE: 'smile',
    OPEN_MOUTH: 'openMouth',
    RAISE_BROWS: 'raiseBrows',
    LOOK_LEFT: 'lookLeft',
    LOOK_RIGHT: 'lookRight',
    LOOK_UP: 'lookUp',
    LOOK_DOWN: 'lookDown',
  };

  const key = actionToKey[action];
  const overrideTiming = runtimeTimingOverrides[key];
  const defaultTiming = DEFAULT_ACTION_TIMINGS[key];

  // Merge override with default (override wins for each field)
  return {
    ...defaultTiming,
    ...overrideTiming,
  };
}

/**
 * Get window time in ms for a specific action
 */
export function getActionWindowMs(action: LivenessAction): number {
  return getActionTiming(action).windowMs;
}

/**
 * Get hold time in ms for a specific action (or 0 if not applicable)
 */
export function getActionHoldMs(action: LivenessAction): number {
  return getActionTiming(action).holdMs ?? 0;
}

/**
 * Get baseline collection time in ms for a specific action (or 0 if not applicable)
 */
export function getActionBaselineMs(action: LivenessAction): number {
  return getActionTiming(action).baselineMs ?? 0;
}

/**
 * Set a runtime override for testing (used by debug page)
 */
export function setThresholdOverride<K extends keyof LivenessThresholds>(
  key: K,
  value: LivenessThresholds[K]
): void {
  runtimeOverrides[key] = value;
}

/**
 * Set a timing override for a specific action
 */
export function setActionTimingOverride(
  action: LivenessAction,
  timing: Partial<ActionTimingConfig>
): void {
  const actionToKey: Record<LivenessAction, keyof ActionTimings> = {
    BLINK: 'blink',
    SMILE: 'smile',
    OPEN_MOUTH: 'openMouth',
    RAISE_BROWS: 'raiseBrows',
    LOOK_LEFT: 'lookLeft',
    LOOK_RIGHT: 'lookRight',
    LOOK_UP: 'lookUp',
    LOOK_DOWN: 'lookDown',
  };

  const key = actionToKey[action];
  runtimeTimingOverrides[key] = {
    ...runtimeTimingOverrides[key],
    ...timing,
  } as ActionTimingConfig;
}

/**
 * Clear all runtime overrides
 */
export function clearThresholdOverrides(): void {
  runtimeOverrides = {};
  runtimeTimingOverrides = {};
}

/**
 * Get all current thresholds (defaults merged with overrides)
 */
export function getCurrentThresholds(): LivenessThresholds {
  return { ...DEFAULT_THRESHOLDS, ...runtimeOverrides };
}

/**
 * Get all current action timings (defaults merged with overrides)
 */
export function getCurrentActionTimings(): ActionTimings {
  return {
    blink: { ...DEFAULT_ACTION_TIMINGS.blink, ...runtimeTimingOverrides.blink },
    smile: { ...DEFAULT_ACTION_TIMINGS.smile, ...runtimeTimingOverrides.smile },
    openMouth: { ...DEFAULT_ACTION_TIMINGS.openMouth, ...runtimeTimingOverrides.openMouth },
    raiseBrows: { ...DEFAULT_ACTION_TIMINGS.raiseBrows, ...runtimeTimingOverrides.raiseBrows },
    lookLeft: { ...DEFAULT_ACTION_TIMINGS.lookLeft, ...runtimeTimingOverrides.lookLeft },
    lookRight: { ...DEFAULT_ACTION_TIMINGS.lookRight, ...runtimeTimingOverrides.lookRight },
    lookUp: { ...DEFAULT_ACTION_TIMINGS.lookUp, ...runtimeTimingOverrides.lookUp },
    lookDown: { ...DEFAULT_ACTION_TIMINGS.lookDown, ...runtimeTimingOverrides.lookDown },
  };
}
